// Reading the JSON the listing prompt returns (scripts/prompts/property_buy_url_new.txt),
// for the listing pages (ListingLookup.js, SavedListings.js). Each standardised
// field is {value, s_value, s_units}, s_value "NA" when unknown.

export const LISTING_PATH = '/listing';
export const SAVED_LISTINGS_PATH = '/reports';
// Where the reports page was before, kept so old links still open it
export const OLD_SAVED_LISTINGS_PATH = '/shortlist';
export const listingPath = (id) => LISTING_PATH + '/' + id;

const SQFT_PER_M2 = 10.764;
// Leases under 80 years cost more to extend (marriage value) and many lenders won't lend on them
export const SHORT_LEASE = 80;
// Under 100 years is worth knowing about: it falls below 80 within a typical mortgage
export const WATCH_LEASE = 100;

// A standardised field's number, or null
export const numberOf = (field) => {
    if (!field) return null;
    if (typeof field.value === 'number' && isFinite(field.value)) return field.value;
    const n = Number(field.s_value);
    return field.s_value && field.s_value !== 'NA' && isFinite(n) ? n : null;
};

export const fmtMoney = (n) => n == null ? null : '£' + Math.round(n).toLocaleString('en-GB');
// "£1,911.92", or "£1,900" for whole pounds
export const fmtPence = (n) => '£' + n.toLocaleString('en-GB', Number.isInteger(n) ? {} : {minimumFractionDigits:2, maximumFractionDigits:2});

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const fmtDay = (iso) => {
    if (!iso) return null;
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    return isNaN(d) ? null : d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
};

// "today", "yesterday", "12 days ago", "3 months ago"
export const ago = (iso, now = new Date()) => {
    if (!iso) return null;
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    if (isNaN(d)) return null;
    const days = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 60) return days + ' days ago';
    const months = Math.round(days / 30.4);
    return months < 24 ? months + ' months ago' : Math.round(days / 365) + ' years ago';
};

export const portalOf = (url) => /zoopla\./i.test(url || '') ? 'Zoopla' : /rightmove\./i.test(url || '') ? 'Rightmove' : 'Listing';

// A UK postcode, "NW10 4QJ"
const POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

const TYPES = {flat:'Flat', terraced:'Terraced house', 'semi-detached':'Semi-detached house', detached:'Detached house',
    bungalow:'Bungalow', other:'Other'};
const TENURES = {freehold:'Freehold', leasehold:'Leasehold', share_of_freehold:'Share of freehold', commonhold:'Commonhold'};

const WORD_NUMBERS = {one:1, two:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9, ten:10};
const countOf = (w) => /^\d+$/.test(w) ? Number(w) : WORD_NUMBERS[w.toLowerCase()];

// Bedrooms or bathrooms from the prompt's field, or for listings read before it asked
// for them, from the key facts ("3 double bedrooms, 1 bathroom")
const roomsOf = (d, field, re) => {
    if (typeof d[field] === 'number' && d[field] >= 0) return d[field];
    for (const f of Array.isArray(d.key_facts) ? d.key_facts : []) {
        const m = typeof f === 'string' && f.match(re);
        if (m) return countOf(m[1]);
    }
    return null;
};
const BEDS = /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)[ -](?:double |single |large )?bed(?:room)?s?\b/i;
const BATHS = /\b(\d+|one|two|three|four|five|six)[ -](?:shower )?bath(?:room)?s?\b/i;
const isStudio = (d) => (Array.isArray(d.key_facts) ? d.key_facts : []).some((f) => /\bstudio\b/i.test(f)) || /\bstudio\b/i.test(d.notes || '');

// The possession, as a key fact, unless the key facts already say it
const possessionFact = (d, facts) => {
    if (d.vacant_possession == null || facts.some((f) => /vacant|chain|tenan|let\b/i.test(f))) return null;
    return d.vacant_possession ? 'Vacant possession, no onward chain' : 'Sold with a tenant in place';
};

/** Everything the page shows, worked out once from the prompt's JSON */
export const readListing = (d) => {
    if (!d) return null;
    const price = numberOf(d.asking_price);
    const size = numberOf(d.size);
    const leaseYears = numberOf(d.leasehold);
    const serviceCharge = numberOf(d.service_charge);
    const groundRent = numberOf(d.ground_rent);
    const isLeasehold = d.tenure === 'leasehold' || (d.leasehold && d.leasehold.s_units === 'LH');
    const loc = d.location || {};
    const postcode = loc.s_units === 'postcode' && POSTCODE.test((loc.s_value || '').trim())
        ? loc.s_value.trim().toUpperCase().replace(/^(.+?) ?(\d[A-Z]{2})$/, '$1 $2') : null;
    const rawType = d.property_type && (d.property_type.value || d.property_type.s_value);
    const share = d.shared_ownership ? (d.shared_ownership_share_pct ||
        (d.asking_price && /^\d+%$/.test(d.asking_price.s_units || '') && d.asking_price.s_units !== '100%' ? parseFloat(d.asking_price.s_units) : null)) : null;

    const warnings = [];
    if (d.shared_ownership) {
        warnings.push({level:'bad', title:'Shared ownership',
            text:(share ? `The price is for a ${share}% share. ` : 'The price is for a share of the home. ') +
                'You pay rent on the rest, and mortgages and resale have extra rules.'});
    }
    if (isLeasehold && leaseYears != null && leaseYears < SHORT_LEASE) {
        warnings.push({level:'bad', title:`Short lease: ${leaseYears} years left`,
            text:'Leases under 80 years are expensive to extend, and many lenders won\'t lend on them.'});
    } else if (isLeasehold && leaseYears != null && leaseYears < WATCH_LEASE) {
        warnings.push({level:'warn', title:`${leaseYears} years left on the lease`,
            text:'It will drop under 80 years within a typical mortgage term, when extending gets much dearer.'});
    } else if (isLeasehold && leaseYears == null) {
        warnings.push({level:'warn', title:'Lease length not stated', text:'Ask the agent how many years are left before you offer.'});
    }
    if (!d.tenure && rawType === 'flat' && !(d.leasehold && d.leasehold.s_units === 'FH')) {
        warnings.push({level:'warn', title:'Tenure not stated',
            text:'Flats are usually leasehold. Ask the agent about the lease length, service charge and ground rent.'});
    }
    const t = d.tenancy || {};
    const rentPcm = typeof t.rent_pcm === 'number' && t.rent_pcm > 0 ? t.rent_pcm : null;
    const isLet = t.currently_let === true || d.vacant_possession === false;
    // Gross yield on the price paid: a year's rent over the asking price
    const grossYield = isLet && rentPcm && price ? rentPcm * 12 / price * 100 : null;
    if (isLet) {
        const rentText = rentPcm
            ? `It is let at ${fmtPence(rentPcm)} a month (${fmtMoney(rentPcm * 12)} a year)${grossYield ? `, a ${grossYield.toFixed(1)}% gross yield on the asking price` : ''}. `
            : 'It is sold with a tenant living there. ';
        warnings.push({level:'warn', title:'Currently let',
            // The details mostly repeat the rent, so they show only when there's no rent to give
            text:rentText + (!rentPcm && t.details ? t.details.replace(/\.?$/, '. ') : '') + "You may not be able to move in straight away."});
    }

    const facts = Array.isArray(d.key_facts) ? d.key_facts.filter((f) => typeof f === 'string' && f.trim()) : [];
    const possession = possessionFact(d, facts);
    const keyFacts = possession ? [...facts, possession] : facts;

    return {
        url:d.url,
        portal:portalOf(d.url),
        address:d.address || (loc.value) || 'Address not found',
        postcode,
        street:!postcode && loc.s_units === 'street' ? loc.s_value : null,
        price,
        priceNote:share ? `for a ${share}% share` : null,
        // A shared ownership price is for the share; per m² is worked out on the whole home
        fullPrice:share && price != null ? price * 100 / share : null,
        size,
        sizeSqft:size != null ? Math.round(size * SQFT_PER_M2) : null,
        pricePerM2:price != null && size && (!d.shared_ownership || share) ? (share ? price * 100 / share : price) / size : null,
        type:TYPES[rawType] || (rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : null),
        tenure:TENURES[d.tenure] || (isLeasehold ? 'Leasehold' : d.leasehold && d.leasehold.s_units === 'FH' ? 'Freehold' : null),
        isLeasehold,
        leaseYears,
        serviceCharge,
        groundRent,
        groundRentPeppercorn:groundRent === 0,
        yearBuilt:d.year_built || null,
        possession:d.vacant_possession === true ? 'Vacant' : isLet ? 'Tenanted' : null,
        isLet, rentPcm, grossYield,
        listed:d.first_listed_date || null,
        agent:d.agent && d.agent.name ? d.agent : null,
        keyFacts,
        bedrooms:roomsOf(d, 'bedrooms', BEDS) ?? (isStudio(d) ? 0 : null),
        bathrooms:roomsOf(d, 'bathrooms', BATHS),
        sources:Array.isArray(d.sources) ? d.sources.filter((s) => typeof s === 'string') : [],
        checked:Array.isArray(d.urls_checked) ? d.urls_checked.filter((c) => c && c.url) : [],
        notes:d.notes || null,
        warnings,
    };
};

// The address with its postcode, for a list row
export const listingTitle = (l) => {
    if (!l) return null;
    if (l.postcode && !l.address.toUpperCase().includes(l.postcode)) return l.address + ', ' + l.postcode;
    return l.address;
};

// A link from a listing or an AI report, only when it is a web address (never javascript: or data:)
export const safeHref = (url) => typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : undefined;

// The period the offer guide's comparable homes sold over, e.g. "Sold between March 2025 and
// August 2026", or null when none has a date: from the earliest comparable to the end of the
// sales data the guide was given (dataTo, the report's sales_data_to), else the latest comparable
export const compsPeriod = (comps, dataTo) => {
    const dates = (Array.isArray(comps) ? comps : []).map((c) => c && c.date).filter((d) => /^\d{4}-\d{2}/.test(d || '')).sort();
    if (!dates.length) return null;
    const month = (d) => new Date(d.slice(0, 7) + '-01T00:00:00').toLocaleDateString('en-GB', {month:'long', year:'numeric'});
    const last = /^\d{4}-\d{2}/.test(dataTo || '') && dataTo > dates[dates.length - 1] ? dataTo : dates[dates.length - 1];
    const a = month(dates[0]), b = month(last);
    return a === b ? `Sold in ${a}` : `Sold between ${a} and ${b}`;
};

export const hostOf = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return url; }
};

// As SavedListings.tidyUrl in the servlet: a Rightmove or Zoopla listing link
export const isListingUrl = (s) => {
    const t = (s || '').trim();
    return /^(https?:\/\/)?(www\.)?rightmove\.co\.uk\/properties\/\d{5,12}([/#?].*)?$/i.test(t)
        || /^(https?:\/\/)?(www\.)?zoopla\.co\.uk\/(for-sale|new-homes|to-rent)\/details\/\d{5,12}([/#?].*)?$/i.test(t);
};

// Whether a listing link says it is a home to rent (true) or for sale (false), or null when it
// doesn't: Zoopla's path, else Rightmove's channel (?channel=RES_LET), as linkSaysRent in the servlet
export const linkSaysRent = (s) => {
    const t = s || '';
    if (/zoopla\.co\.uk\//i.test(t)) return /zoopla\.co\.uk\/to-rent\//i.test(t);
    const m = t.match(/[?&#/]channel=(RES_LET|RES_BUY|RES_NEW)\b/i);
    return m ? m[1].toUpperCase() === 'RES_LET' : null;
};

/* ---------- matching a listing to its home on the Property lookup pages ---------- */

const FLAT_WORDS = /\b(flat|apartment|apt|unit|maisonette|studio)\b/g;
const tidy = (s) => (s || '').toLowerCase().replace(/[’']/g, '').replace(FLAT_WORDS, ' flat ').replace(/[^a-z0-9%]+/g, ' ').trim();
// House and flat numbers, like 86 or 73a; not share percentages (40%) or postcode parts
const numbersIn = (s) => tidy(s).split(' ').filter((t) => /^\d+[a-z]?$/.test(t));
const STOP = new Set(['flat', 'the', 'road', 'rd', 'street', 'st', 'avenue', 'ave', 'lane', 'drive', 'close', 'way', 'house', 'court', 'london']);
const wordsIn = (s) => new Set(tidy(s).split(' ').filter((t) => /^[a-z]{3,}$/.test(t) && !STOP.has(t)));

// The listing's address without its postcode, from address_parts when the prompt gave them
const listingAddress = (d) => {
    const p = d.address_parts || {};
    const parts = [p.flat, p.number, p.building, p.street].filter(Boolean).join(' ');
    const loc = d.location && d.location.s_value;
    const strip = (s) => (s || '').toUpperCase().replace((loc || '#').toUpperCase(), ' ').replace(/\b[A-Z]{1,2}\d[A-Z\d]?(\s*\d[A-Z]{2})?\b/g, ' ');
    return {parts:strip(parts), full:strip(d.address)};
};

/**
 * The one home at the postcode (from /propertylookup homes: [{uprn, address}]) that
 * the listing is, or null. A home matches when its house and flat numbers are exactly
 * the listing's and they share a street or building name; a home with no number, by
 * its name. Only a single match counts: portals often hide the number, and a guess
 * would open the wrong home's page.
 */
export const matchHome = (d, homes) => {
    if (!d || !Array.isArray(homes) || homes.length === 0) return null;
    const {parts, full} = listingAddress(d);
    const source = numbersIn(parts).length ? parts + ' ' + full : full;
    const nums = [...new Set(numbersIn(source))].sort().join(' ');
    const words = wordsIn(source);
    const text = ' ' + tidy(source) + ' ';

    const matches = homes.filter((h) => {
        const hNums = [...new Set(numbersIn(h.address))].sort().join(' ');
        const hWords = wordsIn(h.address);
        const shared = [...hWords].some((w) => words.has(w));
        if (hNums) return hNums === nums && shared;
        // A named home: its name (the address before the street) is in the listing's address
        const name = tidy((h.address || '').split(',')[0]);
        return !nums && name.length > 2 && text.includes(' ' + name + ' ');
    });
    return matches.length === 1 ? matches[0] : null;
};

/**
 * The asking price against the estimated value (valueSeries in property/propertyValue.js),
 * or null. A shared ownership price is scaled up to the whole home first. verdict is
 * below (5% or more under), near, above (5 to 15% over) or high (more than 15% over).
 */
export const askingVsEstimate = (d, series) => {
    const l = readListing(d);
    if (!l || l.price == null || !series || !series.now) return null;
    if (d.shared_ownership && l.fullPrice == null) return null;
    const asking = l.fullPrice != null ? l.fullPrice : l.price;
    const pct = Math.round((asking - series.now) / series.now * 100);
    const size = l.size || series.size;
    return {
        asking, pct, share:l.fullPrice != null,
        verdict:pct <= -5 ? 'below' : pct < 5 ? 'near' : pct <= 15 ? 'above' : 'high',
        askingPpsm:size ? asking / size : null,
    };
};

// The listing link in pasted text, such as a portal's share message ("I found this property
// on the Rightmove Android app and wanted you to see it: https://www.rightmove.co.uk/properties/175034762"),
// else the text as it is
const LINK_IN_TEXT = /(https?:\/\/)?(www\.)?(rightmove\.co\.uk\/properties\/\d{5,12}|zoopla\.co\.uk\/(for-sale|new-homes|to-rent)\/details\/\d{5,12})[^\s"'<>]*/i;
export const extractListingUrl = (text) => {
    const m = (text || '').match(LINK_IN_TEXT);
    if (!m) return text;
    return /^https?:\/\//i.test(m[0]) ? m[0] : 'https://' + m[0];
};

/* ---------- the details form (ListingDetailsForm.js) ---------- */

const CATEGORY = {flat:'flat', terraced:'terraced', 'semi-detached':'semi-detached', detached:'detached', bungalow:'detached'};
const str = (v) => v == null || v === 'NA' ? '' : String(v);

// Dates in the form are typed the UK way, dd/mm/yyyy; saved as yyyy-mm-dd
const ukDate = (iso) => /^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? iso.slice(8, 10) + '/' + iso.slice(5, 7) + '/' + iso.slice(0, 4) : '';
export const isoDate = (uk) => {
    const m = String(uk || '').trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (!m) return null;
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const date = new Date(y, mo - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
    return y + '-' + String(mo).padStart(2, '0') + '-' + String(d).padStart(2, '0');
};
// Puts the slashes in as a date is typed: "2310" -> "23/10"
export const typeUkDate = (s) => {
    const digits = String(s || '').replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/');
};

/** The form's fields, as strings, from a listing's details */
export const detailsToForm = (d) => {
    d = d || {};
    const l = readListing(d) || {};
    const t = d.tenancy || {};
    return {
        price:str(l.price), shared:!!d.shared_ownership, sharePct:str(d.shared_ownership_share_pct),
        type:(d.property_type && d.property_type.value) || '', bedrooms:str(l.bedrooms), bathrooms:str(l.bathrooms),
        size:str(l.size), sizeUnit:'m2', yearBuilt:str(d.year_built),
        tenure:d.tenure || '', leaseYears:str(l.leaseYears), serviceCharge:str(l.serviceCharge), serviceChargePer:'year', groundRent:str(l.groundRent),
        occupancy:t.currently_let || d.vacant_possession === false ? 'let' : d.vacant_possession === true ? 'vacant' : '',
        rent:str(t.rent_pcm), url:d.url || '', listed:ukDate(d.first_listed_date),
        agentName:(d.agent && d.agent.name) || '', agentPhone:(d.agent && d.agent.telephone) || '',
        facts:(Array.isArray(d.key_facts) ? d.key_facts : []).join('\n'),
        // Where it is: changed only through the form's home picker (homeChanged)
        postcode:l.postcode || '', homeUprn:d.home_uprn || null, homeAddress:'', noHome:!!d.no_home, homeChanged:false,
        // The listing's title, shown as its heading: its address unless the user names it
        title:d.address || '',
    };
};

const toNum = (s) => {
    const n = Number(String(s == null ? '' : s).replace(/[£,\s]/g, ''));
    return String(s == null ? '' : s).trim() !== '' && isFinite(n) && n >= 0 ? n : null;
};
const standard = (value, units, unknownUnits = 'NA') => value == null
    ? {value:null, s_value:'NA', s_units:unknownUnits}
    : {value, s_value:String(value), s_units:units};

/** The details to save, in the listing prompt's shape, from the form's fields */
export const formToDetails = (f) => {
    const price = toNum(f.price);
    const share = f.shared ? toNum(f.sharePct) : null;
    let size = toNum(f.size);
    if (size != null && f.sizeUnit === 'sqft') size = size / SQFT_PER_M2;
    if (size != null) size = Math.round(size * 10) / 10;
    const leasehold = f.tenure === 'leasehold' || f.tenure === 'share_of_freehold';
    const years = leasehold ? toNum(f.leaseYears) : null;
    let sc = leasehold ? toNum(f.serviceCharge) : null;
    if (sc != null && f.serviceChargePer === 'month') sc = Math.round(sc * 12 * 100) / 100;
    const gr = leasehold ? toNum(f.groundRent) : null;
    const rent = f.occupancy === 'let' ? toNum(f.rent) : null;
    return {
        ...(f.title && f.title.trim() ? {address:f.title.trim()} : {}),
        asking_price:{value:price, s_value:price == null ? 'NA' : String(price), s_units:share ? share + '%' : '100%'},
        shared_ownership:!!f.shared, shared_ownership_share_pct:share,
        property_type:{value:f.type || null, s_value:CATEGORY[f.type] || null, s_units:'category'},
        bedrooms:toNum(f.bedrooms), bathrooms:toNum(f.bathrooms), year_built:toNum(f.yearBuilt),
        size:standard(size, 'm2'),
        tenure:f.tenure || null,
        leasehold:leasehold ? standard(years, 'LH', 'LH') : f.tenure === 'freehold' || f.tenure === 'commonhold' ? {value:null, s_value:'NA', s_units:'FH'} : standard(null),
        service_charge:standard(sc, 'annual'), ground_rent:standard(gr, 'annual'),
        vacant_possession:f.occupancy === 'vacant' ? true : f.occupancy === 'let' ? false : null,
        tenancy:{currently_let:f.occupancy === 'let' ? true : f.occupancy === 'vacant' ? false : null, rent_pcm:rent, details:null},
        url:f.url.trim() ? (/^https?:\/\//i.test(f.url.trim()) ? f.url.trim() : 'https://' + f.url.trim()) : null,
        first_listed_date:isoDate(f.listed),
        agent:{name:f.agentName.trim() || null, telephone:f.agentPhone.trim() || null},
        key_facts:f.facts.split('\n').map((x) => x.trim()).filter(Boolean),
        ...(f.homeChanged && f.postcode ? {
            location:{value:f.postcode, s_value:f.postcode, s_units:'postcode'},
            home_uprn:f.homeUprn || null,
            no_home:!!f.noHome,
        } : {}),
    };
};
